import { emptyGraph, type SyncGraph, type ThemeConfig } from "@app/core";
import { create } from "zustand";
import { useThemeStore } from "../../stores/theme.store.js";
import {
  clearThemeConflictKeep,
  loadThemeBaseVersion,
  loadThemeConflictKeep,
  loadThemeSyncEnabled,
  saveThemeBaseVersion,
  saveThemeConflictKeep,
} from "./theme-metadata.js";

/** Minimal slice of the sync client the resolver needs. */
interface PushClient {
  push: (changes: SyncGraph) => Promise<{ applied: SyncGraph }>;
}

interface ThemeConflictState {
  /** The remote theme awaiting the user's override-vs-keep decision (null = none). */
  pending: ThemeConfig | null;
  ask: (remote: ThemeConfig) => void;
  /** Adopt the synced theme, discarding this device's divergent changes. */
  override: () => Promise<void>;
  /** Keep this device's theme; suppress the prompt until the remote changes again. */
  keep: () => Promise<void>;
}

export const useThemeConflictStore = create<ThemeConflictState>((set, get) => ({
  pending: null,
  ask: (remote) => set({ pending: remote }),
  async override() {
    const remote = get().pending;
    if (!remote) return;
    await useThemeStore.getState().applyRemoteTheme(remote);
    await saveThemeBaseVersion(remote.updated_at);
    await clearThemeConflictKeep();
    set({ pending: null });
  },
  async keep() {
    const remote = get().pending;
    if (!remote) return;
    await saveThemeConflictKeep(remote.updated_at);
    set({ pending: null });
  },
}));

async function pushLocal(client: PushClient, local: ThemeConfig): Promise<void> {
  await client.push({ ...emptyGraph(), themes: [local] });
  await saveThemeBaseVersion(local.updated_at);
  await clearThemeConflictKeep();
}

/**
 * Conflict-aware theme reconcile (NOT blind LWW — see CLAUDE.md). Run from
 * `syncNow` with the theme pulled from the server. Compares against the
 * last-agreed version (`theme_sync_base`):
 *  - only local changed → push;  only remote changed → adopt;
 *  - both changed → ask the user (override vs keep), remembering "keep" until the
 *    remote version changes again. Sync-disabled devices never push or adopt.
 */
export async function reconcileThemeSync(remote: ThemeConfig | null, client: PushClient): Promise<void> {
  if (!(await loadThemeSyncEnabled())) return;
  await useThemeStore.getState().hydrate();
  const local = useThemeStore.getState().config;
  const base = await loadThemeBaseVersion();

  if (!remote) {
    await pushLocal(client, local); // server has no theme yet → seed it
    return;
  }

  const localDirty = base != null && local.updated_at !== base;
  const remoteChanged = base == null || remote.updated_at !== base;

  if (localDirty && remoteChanged) {
    const keep = await loadThemeConflictKeep();
    if (keep === remote.updated_at) return; // user already chose keep-local for this remote
    useThemeConflictStore.getState().ask(remote);
    return;
  }
  if (remoteChanged) {
    await useThemeStore.getState().applyRemoteTheme(remote);
    await saveThemeBaseVersion(remote.updated_at);
    await clearThemeConflictKeep();
    return;
  }
  if (localDirty) {
    await pushLocal(client, local);
  }
}
